/**
 * useChartGeneration - Hook for generating clinical notes from transcripts
 *
 * Takes a transcript, selected diagnoses, and template to generate
 * a structured clinical note using the AI backend via proxyRunWorkflow.
 * Shaped by: CM7-03; decisions D-CM7-20, D-CM7-21, D-CM7-03-02.
 * Invariants: omitted prompt and template options keep chartmind-notes and the hook template.
 *   Clinical references stay on the base prompt id.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { getDefaultTemplate, parseNoteResponse } from "config/noteTemplates";
import { invokeLLMPrompt } from "services/llmService";
import { retrieveClinicalReferences } from "services/clinicalReferencesService";
import { useAuth } from "hooks/useAuth";

const NOTES_WORKFLOW_NAME = "chartmind-notes";

/**
 * Format selected diagnoses for inclusion in the LLM prompt
 */
const formatDiagnosesForPrompt = (diagnoses) => {
  if (!diagnoses || diagnoses.length === 0) return "";

  const formatted = diagnoses
    .map((dx, i) => {
      let line = `${i + 1}. ${dx.condition}`;
      if (dx.likelihood) line += ` (${dx.likelihood})`;
      if (dx.urgent) line += " [URGENT]";
      if (dx.rationale) line += `\n   Rationale: ${dx.rationale}`;
      return line;
    })
    .join("\n");

  return `\n\nSELECTED DIAGNOSES (confirmed by provider):\n${formatted}`;
};

/**
 * useChartGeneration Hook
 *
 * @param {Object} options
 * @param {Object} options.template - Note template (defaults to SOAP)
 * @param {Object} options.userSettings - Provider settings { specialty, displayName, credentials }
 * @param {Function} options.onDataChanged - Callback fired after successful AI response (for auto-save)
 * @param {string} [options.noteFormatGuidance] - Extra formatting rules appended to this request only
 */
function useChartGeneration({
  template,
  userSettings,
  onDataChanged,
  noteFormatGuidance,
} = {}) {
  const { organizationId } = useAuth();
  const [noteSections, setNoteSections] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawResponse, setRawResponse] = useState(null);
  const [clinicalReferences, setClinicalReferences] = useState([]);
  const [embeddedDocuments, setEmbeddedDocuments] = useState([]);

  // Use provided template or default SOAP
  const activeTemplate = template || getDefaultTemplate();

  // Track whether the last noteSections change came from a user edit (updateSection)
  // vs. AI generation (generateNote already calls onDataChanged synchronously).
  const pendingUserEditRef = useRef(false);

  // After updateSection's setNoteSections flushes, fire onDataChanged with fresh state.
  useEffect(() => {
    if (pendingUserEditRef.current && noteSections && onDataChanged) {
      pendingUserEditRef.current = false;
      onDataChanged();
    }
  }, [noteSections, onDataChanged]);

  const upsertFeedback = useCallback((updates) => {
    setFeedback((prev) => {
      const next = {
        ...(prev || {}),
        ...updates,
      };

      const hasRating =
        typeof next.rating === "number" && Number.isFinite(next.rating) && next.rating > 0;
      const hasRemarks =
        typeof next.remarks === "string" && next.remarks.trim().length > 0;

      if (!hasRating && !hasRemarks) {
        return null;
      }

      return {
        ...(hasRating ? { rating: next.rating } : {}),
        ...(hasRemarks ? { remarks: next.remarks } : {}),
        updatedAt: new Date().toISOString(),
      };
    });
  }, []);

  /**
   * Generate clinical note from transcript and diagnoses
   *
   * @param {string} transcript - The encounter transcript
   * @param {Array} selectedDiagnoses - Array of selected diagnosis objects
   * @param {Object} [callOptions] - Optional per-call template and prompt ids
   */
  const generateNote = useCallback(
    async (transcript, selectedDiagnoses = [], callOptions = null) => {
      if (!transcript || transcript.trim().length < 50) {
        setError("Transcript is too short. Need at least 50 characters.");
        return null;
      }

      setLoading(true);
      setError(null);
      setRawResponse(null);

      try {
        // Build the query with all context for the backend workflow
        const noteTemplate = callOptions?.template || activeTemplate;
        const promptId = callOptions?.promptIdOverride || NOTES_WORKFLOW_NAME;
        const diagnosisInfo = formatDiagnosesForPrompt(selectedDiagnoses);
        const guidanceInfo = noteTemplate.guidance
          ? `\nGUIDANCE: ${noteTemplate.guidance}`
          : "";
        const templateInfo = `NOTE TEMPLATE: ${
          noteTemplate.name
        }\nSECTIONS: ${noteTemplate.sections.map((s) => s.key).join(", ")}${guidanceInfo}`;
        const formatInfo =
          typeof noteFormatGuidance === "string" && noteFormatGuidance.trim()
            ? `\n\n${noteFormatGuidance.trim()}`
            : "";
        const query = `${templateInfo}${formatInfo}\n\nPATIENT ENCOUNTER TRANSCRIPT:\n\n${transcript}${diagnosisInfo}`;

        console.log("[useChartGeneration] 📝 GENERATING NOTE VIA WORKFLOW...", {
          workflowName: promptId,
          templateName: noteTemplate.name,
          sectionsCount: noteTemplate.sections.length,
          transcriptLength: transcript.length,
          diagnosesCount: selectedDiagnoses.length,
          timestamp: new Date().toISOString(),
        });
        // Call workflow API
        const [result, refs] = await Promise.all([
          invokeLLMPrompt({
            promptId,
            userMessage: query,
            organizationId,
            includeEmbeddedDocuments: true,
            embeddedDocumentsOptions: { chunkLimit: 8 },
            ...(callOptions?.fallbackPromptId
              ? { fallbackPromptId: callOptions.fallbackPromptId }
              : {}),
          }),
          // D-CM7-03-02: reference attachments are indexed on the base prompt id.
          retrieveClinicalReferences(transcript, {
            promptId: NOTES_WORKFLOW_NAME,
          }).catch((err) => {
            console.warn("[useChartGeneration] Clinical references retrieval failed:", err);
            return [];
          }),
        ]);

        setClinicalReferences(refs);
        setEmbeddedDocuments(result.embeddedDocuments?.documents ?? []);

        const responseText = result.output;
        setRawResponse(responseText);

        console.log("[useChartGeneration] Response received", {
          responseLength: responseText?.length || 0,
          preview: responseText?.substring(0, 200),
        });
        // Parse the response. When the org supplied custom prompt rules, honor
        // an "omit empty sections" intent by blanking out sections the model
        // filled with "not documented" text against instructions.
        const omitEmptySections = Boolean(result.meta?.organizationRulesApplied);
        const parsed = parseNoteResponse(responseText, noteTemplate.sections, {
          omitEmptySections,
        });

        if (parsed) {
          setNoteSections(parsed);
          setFeedback(null);
          console.log("[useChartGeneration] Note generated successfully", {
            sections: Object.keys(parsed),
          });

          // Trigger auto-save after successful AI response
          if (onDataChanged) {
            console.log("[useChartGeneration] Triggering auto-save callback");
            onDataChanged();
          }

          return parsed;
        } else {
          setError(
            "Failed to parse note response. See raw response for details.",
          );
          return null;
        }
      } catch (err) {
        console.error("[useChartGeneration] Error:", err);
        setError(err.message || "Failed to generate clinical note");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [activeTemplate, userSettings, onDataChanged, organizationId, noteFormatGuidance],
  );

  /**
   * Update a specific section of the note
   */
  const updateSection = useCallback((sectionKey, value) => {
    pendingUserEditRef.current = true;
    setNoteSections((prev) => ({
      ...prev,
      [sectionKey]: value,
    }));
  }, []);

  /**
   * Store chart feedback for the current note draft
   */
  const updateFeedbackRating = useCallback((rating) => {
    upsertFeedback({
      rating:
        typeof rating === "number" && Number.isFinite(rating) && rating > 0
          ? rating
          : null,
    });
  }, [upsertFeedback]);

  /**
   * Store free-text remarks for the current note draft
   */
  const updateFeedbackRemarks = useCallback((remarks) => {
    upsertFeedback({
      remarks: typeof remarks === "string" ? remarks : "",
    });
  }, [upsertFeedback]);

  /**
   * Clear all note data
   */
  const clearNote = useCallback(() => {
    setNoteSections(null);
    setFeedback(null);
    setError(null);
    setRawResponse(null);
    setClinicalReferences([]);
    setEmbeddedDocuments([]);
  }, []);

  /**
   * Format note for clipboard (plain text)
   */
  const formatForClipboard = useCallback(() => {
    if (!noteSections) return "";

    let text = `${activeTemplate.name.toUpperCase()}\n${"=".repeat(
      activeTemplate.name.length + 5,
    )}\n\n`;

    activeTemplate.sections.forEach((section) => {
      const content = noteSections[section.key] || "";
      text += `${section.title.toUpperCase()}:\n${content}\n\n`;
    });

    text += `---\nGenerated by ChartMind\n${new Date().toLocaleString()}`;
    return text;
  }, [noteSections, activeTemplate]);

  /**
   * Directly set noteSections (for hydrating from saved session)
   */
  const setNoteSectionsDirectly = useCallback((sections) => {
    console.log(
      "[useChartGeneration] setNoteSectionsDirectly called with:",
      sections,
    );
    setNoteSections(sections);
  }, []);

  /**
   * Directly set feedback (for hydrating from saved session)
   */
  const setFeedbackDirectly = useCallback((nextFeedback) => {
    setFeedback(nextFeedback || null);
  }, []);

  /**
   * Restore source documents (for hydrating from saved session).
   * Resets to empty when the saved session has none.
   */
  const hydrateSourceDocuments = useCallback((data) => {
    setClinicalReferences(data?.clinicalReferences || []);
    setEmbeddedDocuments(data?.embeddedDocuments || []);
  }, []);

  return {
    // State
    noteSections,
    feedback,
    loading,
    error,
    rawResponse,
    clinicalReferences,
    embeddedDocuments,
    template: activeTemplate,

    // Computed
    hasNote: !!noteSections,

    // Actions
    generateNote,
    updateSection,
    updateFeedbackRating,
    updateFeedbackRemarks,
    clearNote,
    formatForClipboard,
    setNoteSections: setNoteSectionsDirectly, // For session hydration
    setFeedback: setFeedbackDirectly, // For session hydration
    hydrateSourceDocuments, // For session hydration
  };
}

export default useChartGeneration;
